import type { LaseredCondition } from "@/types";
import type { DeductibleResolutionEvent } from "@/types/deductibleReview";
import {
  conditionStateCountsText,
  expectedTriggerNotApplied,
  type ExpectedTriggerTermsWindows,
} from "@/utils/helpers/expectedTrigger";

/**
 * Per-condition timeline from the claimant's deductible resolution history
 * (#3513, #3506). Triggers can reverse, so a condition's history is a sequence
 * of: triggered (the deductible moved to it), re-dated (still it, a different
 * date of service: earlier, e.g. a back-dated line, or later, e.g. the earliest
 * line went away), changed (still it, same date, a new amount), and cleared (the
 * deductible moved away from it, e.g. the line was deleted or a later term won).
 *
 * A review decision that chose this condition (`review_decision` with the
 * condition as `source_ref_id`) keeps it in force and is labelled "chosen in
 * review" (#7346).
 *
 * Expected triggers (#7361): an event with `is_expected_trigger` is the
 * deductible moving to an expected trigger ("expected"); the next event for the
 * condition without it is the real claim line replacing it ("replaced"). The
 * condition's own expected-trigger record adds who set it and why, when it was
 * cancelled, and when it went overdue, which need not move the deductible.
 *
 * Events arrive newest first; this walks them oldest first.
 */
export type ConditionTimelineKind =
  | "triggered"
  | "redated"
  | "changed"
  | "reviewed"
  | "cleared"
  | "expected"
  | "replaced"
  | "cancelled"
  | "overdue";

export interface ConditionTimelineEntry {
  kind: ConditionTimelineKind;
  /** When it was recorded; "" when unknown (an arrival without its event). */
  at: string;
  amountBefore?: number;
  /** Absent for entries that did not move the deductible (expected-trigger record entries). */
  amountAfter?: number;
  /** For `overdue`: the review-by date. */
  effectiveDate?: string;
  /** For `redated`: the date it was in force from before. */
  previousEffectiveDate?: string;
  /** For `redated`: which way the date moved. */
  direction?: "earlier" | "later";
  /** The event is a review decision that chose this condition. */
  chosenInReview?: boolean;
  triggeringClaimItemId?: string;
  /** For `cleared`: where the deductible went. */
  sourceAfter?: string;
  /** Expected-trigger entries: the reason it was set, or why it was cancelled. */
  reason?: string;
  /** Expected-trigger entries: who set or cancelled it. */
  by?: string;
}

const ymd = (d?: string) => (d ? d.slice(0, 10) : undefined);

/** Sort key: unknown times last (newest once reversed). */
const time = (at: string) => {
  const t = Date.parse(at);
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
};

export const buildConditionTimeline = (
  conditionId: string,
  events: DeductibleResolutionEvent[],
  condition?: Pick<LaseredCondition, "expected_trigger" | "trigger_date" | "triggered_by_claim_item_id"> &
    ExpectedTriggerTermsWindows
): ConditionTimelineEntry[] => {
  const et = condition?.expected_trigger;
  const ordered = [...events].sort((a, b) => a.created_datetime.localeCompare(b.created_datetime));
  const out: ConditionTimelineEntry[] = [];
  let inForce: { effectiveDate?: string; amount: number; expected: boolean } | undefined;
  // Whether the current expected trigger (the record) has an event, and whether its arrival has one.
  let currentSetShown = false;
  let replacedShown = false;
  // The record describes the latest expected trigger only: its reason and user
  // go on the event with its date, recorded after it was set.
  const isCurrentSet = (e: DeductibleResolutionEvent) =>
    !!et &&
    ymd(e.effective_date) === ymd(et.expected_date) &&
    (!et.set_datetime || time(e.created_datetime) >= time(et.set_datetime));

  for (const e of ordered) {
    const chosenInReview = e.source_after === "review_decision";
    const isThis = (e.source_after === "conditional_laser" || chosenInReview) && e.source_ref_id === conditionId;
    if (isThis) {
      const expected = !!e.is_expected_trigger;
      const base = {
        at: e.created_datetime,
        amountBefore: e.amt_deductible_before,
        amountAfter: e.amt_deductible_after,
        effectiveDate: e.effective_date,
        triggeringClaimItemId: e.triggering_claim_item_id,
        ...(chosenInReview && { chosenInReview: true }),
      };
      const before = ymd(inForce?.effectiveDate);
      const after = ymd(e.effective_date);
      if (expected && !inForce?.expected) {
        const current = isCurrentSet(e);
        if (current) currentSetShown = true;
        // An earlier expected trigger (since replaced or cancelled) carries its own
        // reason and setter on the event, not the condition's current one (!4881
        // thread 30548).
        const reason = current && et ? et.reason : e.expected_trigger_reason;
        const by = current && et ? et.set_by : e.expected_trigger_set_by;
        out.push({ kind: "expected", ...base, ...(reason && { reason }), ...(by && { by }) });
      } else if (!expected && inForce?.expected && !chosenInReview) {
        replacedShown = true;
        out.push({
          kind: "replaced",
          ...base,
          amountBefore: inForce.amount,
          ...(et?.state === "arrived" && { reason: et.reason }),
        });
      } else if (!inForce) {
        out.push({ kind: "triggered", ...base });
      } else if (before && after && before !== after) {
        out.push({
          kind: "redated",
          ...base,
          previousEffectiveDate: inForce.effectiveDate,
          direction: after < before ? "earlier" : "later",
        });
      } else if (e.amt_deductible_after !== inForce.amount) {
        out.push({ kind: "changed", ...base, amountBefore: inForce.amount });
      } else if (chosenInReview) {
        out.push({ kind: "reviewed", ...base });
      }
      inForce = {
        effectiveDate: e.effective_date ?? inForce?.effectiveDate,
        amount: e.amt_deductible_after,
        expected: chosenInReview ? (inForce?.expected ?? false) || expected : expected,
      };
    } else if (inForce) {
      out.push({
        kind: "cleared",
        at: e.created_datetime,
        amountBefore: e.amt_deductible_before,
        amountAfter: e.amt_deductible_after,
        effectiveDate: e.effective_date,
        sourceAfter: e.source_after,
      });
      inForce = undefined;
    }
  }

  // The expected-trigger record: set (when the deductible did not move, e.g. a
  // later trigger already wins), cancelled, overdue, and an arrival with no event.
  if (et) {
    if (!currentSetShown && et.set_datetime) {
      out.push({
        kind: "expected",
        at: et.set_datetime,
        effectiveDate: et.expected_date,
        reason: et.reason,
        by: et.set_by,
      });
    }
    if (et.state === "cancelled" && et.cancelled_datetime) {
      out.push({ kind: "cancelled", at: et.cancelled_datetime, reason: et.cancel_reason, by: et.cancelled_by });
    }
    // Outside the incurred window it is not applied, so no overdue review opens.
    if (et.state === "overdue" && !expectedTriggerNotApplied(condition)) {
      out.push({ kind: "overdue", at: et.review_by, effectiveDate: et.review_by });
    }
    if (et.state === "arrived" && !replacedShown) {
      out.push({
        kind: "replaced",
        at: "",
        effectiveDate: condition?.trigger_date,
        triggeringClaimItemId: condition?.triggered_by_claim_item_id,
        reason: et.reason,
      });
    }
  }

  // Stable: events keep their order; record entries fall in by time.
  return out.sort((a, b) => time(a.at) - time(b.at)).reverse();
};

const SOURCE_TEXT: Record<string, string> = {
  specific: "the policy base deductible",
  lasered: "the straight laser",
  conditional_laser: "another conditional laser",
  modification: "a deductible modification",
  contract_laser: "the contract laser",
  contract_excluded: "the contract exclusion",
  review_decision: "a review decision",
};

export const clearedReason = (sourceAfter?: string) =>
  sourceAfter ? `now ${SOURCE_TEXT[sourceAfter] ?? sourceAfter}` : "no longer in force";

/** "2 conditional lasers: 1 armed, 1 expected" (#3513 AC3, #7361). Lived on the old conditions section; kept for the Deductible tab and its tests. */
export const conditionsSummaryLine = (conditions: LaseredCondition[]): string => {
  const parts = conditionStateCountsText(conditions);
  const noun = conditions.length === 1 ? "conditional laser" : "conditional lasers";
  return `${conditions.length} ${noun}${parts ? `: ${parts}` : ""}`;
};
